import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { CheckCircle2, Clock, XCircle, AlertCircle, Shield, Calendar, Building2, Loader2, ChevronRight } from 'lucide-react';
import logoWhite from '../assets/hazdex-side-orange-wht.png';

const API_ENDPOINT = process.env.REACT_APP_API_ENDPOINT;

const STATUS_OPTIONS = [
  {
    value: 'in_progress',
    label: 'In Progress',
    description: 'I am actively working on this',
    icon: Clock,
    color: '#d97706',
    bg: '#fffbeb',
    border: '#fde68a',
  },
  {
    value: 'completed',
    label: 'Completed',
    description: 'This action has been fully implemented',
    icon: CheckCircle2,
    color: '#16a34a',
    bg: '#f0fdf4',
    border: '#bbf7d0',
  },
  {
    value: 'unachievable',
    label: 'Unachievable',
    description: 'This cannot be completed — please explain why',
    icon: XCircle,
    color: '#64748b',
    bg: '#f8fafc',
    border: '#e2e8f0',
  },
];

const PRIORITY_CONFIG = {
  High:   { color: '#dc2626', bg: '#fef2f2', label: 'High Priority' },
  Medium: { color: '#d97706', bg: '#fffbeb', label: 'Medium Priority' },
  Low:    { color: '#16a34a', bg: '#f0fdf4', label: 'Low Priority' },
};

const formatDate = (dateStr) => {
  if (!dateStr) return null;
  try {
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'long', day: 'numeric', year: 'numeric'
    });
  } catch { return dateStr; }
};

export default function PublicCAUpdate() {
  const { token } = useParams();

  const [ca, setCA]               = useState(null);
  const [loading, setLoading]     = useState(true);
  const [error, setError]         = useState(null);
  const [submitted, setSubmitted] = useState(false);

  // Form state
  const [selectedStatus, setSelectedStatus] = useState('');
  const [notes, setNotes]                   = useState('');
  const [completionDate, setCompletionDate] = useState('');
  const [recipientName, setRecipientName]   = useState('');
  const [saving, setSaving]                 = useState(false);
  const [formError, setFormError]           = useState('');

  useEffect(() => {
    const fetchCA = async () => {
      try {
        const res = await fetch(`${API_ENDPOINT}/ca-update/${token}`);
        if (!res.ok) {
          const data = await res.json().catch(() => ({}));
          throw new Error(data.error || 'This link is invalid or has expired.');
        }
        const data = await res.json();
        setCA(data);
        // Pre-fill status if there's a pending update
        if (data.recipient_update?.status) {
          setSelectedStatus(data.recipient_update.status);
          setNotes(data.recipient_update.notes || '');
          setCompletionDate(data.recipient_update.completion_date || '');
          setRecipientName(data.recipient_update.recipient_name || '');
        }
      } catch (e) {
        setError(e.message);
      } finally {
        setLoading(false);
      }
    };
    fetchCA();
  }, [token]);

  const handleSubmit = async () => {
    setFormError('');

    if (!selectedStatus) {
      setFormError('Please select a status.');
      return;
    }
    if (!notes.trim()) {
      setFormError('Notes are required — please describe the current situation or progress.');
      return;
    }
    if (selectedStatus === 'completed' && !completionDate) {
      setFormError('Please provide the completion date.');
      return;
    }

    setSaving(true);
    try {
      const res = await fetch(`${API_ENDPOINT}/ca-update/${token}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          status:          selectedStatus,
          notes:           notes.trim(),
          completion_date: completionDate || undefined,
          recipient_name:  recipientName.trim() || undefined,
        }),
      });

      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.error || 'Failed to submit update.');
      }

      setSubmitted(true);
    } catch (e) {
      setFormError(e.message);
    } finally {
      setSaving(false);
    }
  };

  // ── Loading ───────────────────────────────────────────────────────────────

  if (loading) {
    return (
      <div style={pageStyle}>
        <div style={cardStyle}>
          <HAZDEXHeader />
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '48px 0', gap: 12 }}>
            <Loader2 size={28} color="#002855" style={{ animation: 'spin 1s linear infinite' }} />
            <p style={{ color: '#64748b', fontSize: 14, margin: 0 }}>Loading corrective action...</p>
          </div>
          <style>{spinStyle}</style>
        </div>
      </div>
    );
  }

  // ── Error ─────────────────────────────────────────────────────────────────

  if (error) {
    return (
      <div style={pageStyle}>
        <div style={cardStyle}>
          <HAZDEXHeader />
          <div style={{ padding: '40px 32px', textAlign: 'center' }}>
            <div style={{ width: 56, height: 56, borderRadius: '50%', background: '#fef2f2', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px' }}>
              <AlertCircle size={28} color="#dc2626" />
            </div>
            <h2 style={{ margin: '0 0 8px', fontSize: 18, fontWeight: 700, color: '#0f172a' }}>Link Unavailable</h2>
            <p style={{ margin: 0, fontSize: 14, color: '#64748b', lineHeight: 1.6 }}>{error}</p>
          </div>
        </div>
      </div>
    );
  }

  // ── Success ───────────────────────────────────────────────────────────────

  if (submitted) {
    const opt = STATUS_OPTIONS.find(o => o.value === selectedStatus);
    return (
      <div style={pageStyle}>
        <div style={cardStyle}>
          <HAZDEXHeader />
          <div style={{ padding: '40px 32px', textAlign: 'center' }}>
            <div style={{
              width: 64, height: 64, borderRadius: '50%',
              background: opt?.bg || '#f0fdf4',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              margin: '0 auto 20px',
              border: `2px solid ${opt?.border || '#bbf7d0'}`,
            }}>
              {opt && <opt.icon size={32} color={opt.color} />}
            </div>
            <h2 style={{ margin: '0 0 8px', fontSize: 20, fontWeight: 800, color: '#0f172a' }}>
              Update Submitted
            </h2>
            <p style={{ margin: '0 0 20px', fontSize: 14, color: '#64748b', lineHeight: 1.6 }}>
              Your update has been received and is pending review by the HAZDEX administrator.
              You will be notified if any follow-up is needed.
            </p>
            <div style={{
              padding: '14px 20px', background: opt?.bg || '#f0fdf4',
              borderRadius: 8, border: `1px solid ${opt?.border || '#bbf7d0'}`,
              display: 'inline-block', textAlign: 'left',
            }}>
              <p style={{ margin: '0 0 4px', fontSize: 12, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: 0.5 }}>
                Status submitted
              </p>
              <p style={{ margin: 0, fontSize: 15, fontWeight: 700, color: opt?.color || '#16a34a' }}>
                {opt?.label || selectedStatus}
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ── Already completed ─────────────────────────────────────────────────────

  if (ca.is_completed) {
    return (
      <div style={pageStyle}>
        <div style={cardStyle}>
          <HAZDEXHeader ca={ca} />
          <div style={{ padding: '40px 32px', textAlign: 'center' }}>
            <div style={{ width: 56, height: 56, borderRadius: '50%', background: '#f0fdf4', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px', border: '2px solid #bbf7d0' }}>
              <CheckCircle2 size={28} color="#16a34a" />
            </div>
            <h2 style={{ margin: '0 0 8px', fontSize: 18, fontWeight: 700, color: '#0f172a' }}>Already Completed</h2>
            <p style={{ margin: 0, fontSize: 14, color: '#64748b', lineHeight: 1.6 }}>
              This corrective action has been marked as completed and is no longer accepting updates.
            </p>
          </div>
          <CADetailCard ca={ca} />
        </div>
      </div>
    );
  }

  const priority = PRIORITY_CONFIG[ca.priority] || PRIORITY_CONFIG.Medium;

  // ── Main Form ─────────────────────────────────────────────────────────────

  return (
    <div style={pageStyle}>
      <style>{spinStyle}</style>
      <div style={cardStyle}>
        <HAZDEXHeader ca={ca} />

        {/* Previous update notice */}
        {/* Meta strip */}
        <div style={{ padding: '14px 32px', background: '#f8fafc', borderBottom: '1px solid #e2e8f0', display: 'flex', gap: 16, flexWrap: 'wrap', alignItems: 'center' }}>
          <span style={{ padding: '3px 10px', borderRadius: 20, fontSize: 11, fontWeight: 700, color: priority.color, background: priority.bg }}>
            {priority.label}
          </span>
          {ca.nims_capability && ca.nims_capability !== 'General' && (
            <span style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 12, color: '#64748b' }}>
              <Shield size={11} /> {ca.nims_capability}
            </span>
          )}
          {ca.assigned_org_name && (
            <span style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 12, color: '#64748b' }}>
              <Building2 size={11} /> {ca.assigned_org_name}
            </span>
          )}
          {ca.due_date && (
            <span style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 12, color: new Date(ca.due_date) < new Date() ? '#dc2626' : '#64748b', fontWeight: new Date(ca.due_date) < new Date() ? 700 : 400 }}>
              <Calendar size={11} />
              Due: {formatDate(ca.due_date)}
              {new Date(ca.due_date) < new Date() && ' — OVERDUE'}
            </span>
          )}
          {ca.incident_name && (
            <span style={{ fontSize: 12, color: '#94a3b8' }}>From: {ca.incident_name}</span>
          )}
        </div>
        {ca.recipient_update_status === 'pending_review' && (
          <div style={{ margin: '0 32px', marginTop: 20, padding: '12px 16px', background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 8 }}>
            <p style={{ margin: 0, fontSize: 13, color: '#d97706', fontWeight: 600 }}>
              ⏳ You previously submitted an update — it is pending review by the administrator.
              You can submit a revised update below if needed.
            </p>
          </div>
        )}

        {/* Update Form */}
        <div style={{ padding: '24px 32px' }}>
          <h3 style={{ margin: '0 0 4px', fontSize: 16, fontWeight: 700, color: '#0f172a' }}>
            Submit Your Update
          </h3>
          <p style={{ margin: '0 0 20px', fontSize: 13, color: '#64748b' }}>
            All fields marked with notes are required. Your update will be reviewed before being applied.
          </p>

          {/* Your name */}
          <div style={{ marginBottom: 16 }}>
            <label style={labelStyle}>Your Name</label>
            <input
              value={recipientName}
              onChange={e => setRecipientName(e.target.value)}
              placeholder="Optional — helps the administrator identify who submitted"
              style={inputStyle}
            />
          </div>

          {/* Status selection */}
          <div style={{ marginBottom: 16 }}>
            <label style={labelStyle}>Status Update *</label>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {STATUS_OPTIONS.map(opt => {
                const Icon = opt.icon;
                const isSelected = selectedStatus === opt.value;
                return (
                  <button
                    key={opt.value}
                    onClick={() => setSelectedStatus(opt.value)}
                    style={{
                      display: 'flex', alignItems: 'center', gap: 12,
                      padding: '14px 16px', borderRadius: 8, cursor: 'pointer',
                      border: `2px solid ${isSelected ? opt.color : '#e2e8f0'}`,
                      background: isSelected ? opt.bg : '#fff',
                      textAlign: 'left', transition: 'all 0.15s',
                    }}
                  >
                    <div style={{
                      width: 36, height: 36, borderRadius: '50%',
                      background: isSelected ? opt.color : '#f1f5f9',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      flexShrink: 0, transition: 'all 0.15s',
                    }}>
                      <Icon size={18} color={isSelected ? '#fff' : '#94a3b8'} />
                    </div>
                    <div>
                      <p style={{ margin: 0, fontSize: 14, fontWeight: 700, color: isSelected ? opt.color : '#374151' }}>
                        {opt.label}
                      </p>
                      <p style={{ margin: 0, fontSize: 12, color: isSelected ? opt.color : '#94a3b8' }}>
                        {opt.description}
                      </p>
                    </div>
                    {isSelected && (
                      <div style={{ marginLeft: 'auto' }}>
                        <ChevronRight size={16} color={opt.color} />
                      </div>
                    )}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Completion date — only when completed */}
          {selectedStatus === 'completed' && (
            <div style={{ marginBottom: 16 }}>
              <label style={labelStyle}>Completion Date *</label>
              <input
                type="date"
                value={completionDate}
                onChange={e => setCompletionDate(e.target.value)}
                style={inputStyle}
              />
            </div>
          )}

          {/* Notes — always required */}
          <div style={{ marginBottom: 20 }}>
            <label style={labelStyle}>
              Notes *
              {selectedStatus === 'unachievable' && (
                <span style={{ color: '#dc2626', marginLeft: 6, fontWeight: 400, textTransform: 'none' }}>
                  — required when marking unachievable
                </span>
              )}
            </label>
            <textarea
              value={notes}
              onChange={e => setNotes(e.target.value)}
              placeholder={
                selectedStatus === 'completed'
                  ? 'Describe what was done and how the action was implemented...'
                  : selectedStatus === 'unachievable'
                  ? 'Explain why this corrective action cannot be completed...'
                  : 'Describe your current progress, any blockers, or relevant updates...'
              }
              style={{ ...inputStyle, minHeight: 100, resize: 'vertical' }}
            />
          </div>

          {/* Error */}
          {formError && (
            <div style={{ marginBottom: 16, padding: '10px 14px', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 8 }}>
              <p style={{ margin: 0, fontSize: 13, color: '#dc2626' }}>{formError}</p>
            </div>
          )}

          {/* Submit */}
          <button
            onClick={handleSubmit}
            disabled={saving}
            style={{
              width: '100%', padding: '14px', borderRadius: 8,
              fontSize: 15, fontWeight: 700,
              background: saving ? '#94a3b8' : '#002855',
              color: '#fff', border: 'none',
              cursor: saving ? 'not-allowed' : 'pointer',
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
              transition: 'background 0.15s',
            }}
          >
            {saving
              ? <><Loader2 size={16} style={{ animation: 'spin 1s linear infinite' }} /> Submitting...</>
              : 'Submit Update'
            }
          </button>

          <p style={{ margin: '12px 0 0', fontSize: 11, color: '#94a3b8', textAlign: 'center', lineHeight: 1.5 }}>
            Your update will be reviewed by the HAZDEX administrator before being applied.
            This link is unique to this corrective action.
          </p>
        </div>
      </div>
    </div>
  );
}

// ── Sub-components ────────────────────────────────────────────────────────────

function HAZDEXHeader({ ca }) {
  return (
    <div style={{
      background: '#002855', padding: '20px 32px',
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: ca ? 16 : 0 }}>
        <img
          src={logoWhite}
          alt="HAZDEX"
          style={{ height: 48, width: 'auto' }}
        />
        <div>
          <p style={{ margin: 0, fontSize: 11, color: '#C8D8F0', letterSpacing: 1, textTransform: 'uppercase' }}>
            Corrective Action Update
          </p>
        </div>
      </div>
      {ca && (
        <div style={{ borderTop: '1px solid rgba(255,255,255,0.15)', paddingTop: 16 }}>
          <p style={{ margin: '0 0 6px', fontSize: 11, fontWeight: 700, color: '#C8D8F0', textTransform: 'uppercase', letterSpacing: 0.5 }}>
            Corrective Action
          </p>
          <p style={{ margin: '0 0 6px', fontSize: 17, fontWeight: 800, color: '#ffffff', lineHeight: 1.3, wordBreak: 'break-word' }}>
            {ca.title}
          </p>
          <p style={{ margin: 0, fontSize: 13, color: '#C8D8F0', lineHeight: 1.5 }}>
            {ca.description}
          </p>
        </div>
      )}
    </div>
  );
}

function CADetailCard({ ca }) {
  return (
    <div style={{ padding: '20px 32px', borderTop: '1px solid #e2e8f0' }}>
      <p style={{ margin: '0 0 6px', fontSize: 12, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: 0.5 }}>
        Corrective Action
      </p>
      <p style={{ margin: 0, fontSize: 15, fontWeight: 700, color: '#0f172a' }}>{ca.title}</p>
    </div>
  );
}

// ── Styles ────────────────────────────────────────────────────────────────────

const pageStyle = {
  minHeight: '100vh',
  background: 'linear-gradient(135deg, #EBF0F7 0%, #f8fafc 100%)',
  display: 'flex', alignItems: 'flex-start', justifyContent: 'center',
  padding: '40px 16px',
  fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
};

const cardStyle = {
  background: '#fff', borderRadius: 12, width: '100%', maxWidth: 580,
  boxShadow: '0 8px 32px rgba(0,40,85,0.12)',
  overflow: 'hidden',
};

const labelStyle = {
  display: 'block', fontSize: 12, fontWeight: 700, color: '#374151',
  marginBottom: 6, textTransform: 'uppercase', letterSpacing: 0.4,
};

const inputStyle = {
  width: '100%', padding: '10px 14px', borderRadius: 8,
  border: '1.5px solid #e2e8f0', fontSize: 14, color: '#0f172a',
  background: '#fff', outline: 'none', boxSizing: 'border-box',
  fontFamily: 'inherit', transition: 'border-color 0.15s',
};

const spinStyle = `@keyframes spin { from { transform: rotate(0deg) } to { transform: rotate(360deg) } }`;